import { useState, useEffect, useCallback } from "react";
import { FileText, Rss, Waypoints } from "lucide-react";
import { getSharedWithMe } from "../../../api/sharing";
import { useSearchRegistry } from "./SearchRegistry";

/**
 * useSharedSearchItems
 *
 * Loads shared-with-me data and registers items into per-section registry keys
 * (e.g. "__shared:/reports") so MainSearch can merge them inline when drilling.
 * Items include proper icons and sub-directories matching their section.
 *
 * Mount this hook once in SidebarPortals.jsx.
 */
export default function useSharedSearchItems() {
  const [sharedData, setSharedData] = useState(null);
  const { register, unregister } = useSearchRegistry();

  const load = useCallback(async () => {
    try {
      const res = await getSharedWithMe();
      setSharedData(res?.data || {});
    } catch (e) {
      console.error("Error loading shared items for search:", e);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener("sidebar:refresh-shared", handler);
    return () => window.removeEventListener("sidebar:refresh-shared", handler);
  }, [load]);

  useEffect(() => {
    if (!sharedData) return;

    const sections = {
      "/reports": [],
      "/feed": [],
      "/graph": [],
      "/wiki": [],
    };

    // ── Reports: same icon as ReportsSidebarPortal ──
    (sharedData.reports || []).forEach((r) => {
      sections["/reports"].push({
        id: `shared-report-${r.report_id || r.document_id}`,
        label: r.title || "Untitled Report",
        keywords: ["shared", r.owner_name || ""],
        shared: true,
        sharedBy: r.owner_name || "Unknown",
        icon: FileText,
        event: {
          navigateTo: "/reports",
          name: "sidebar:report-selected",
          detail: { reportId: r.report_id || r.document_id },
        },
      });
    });

    // ── Feed views ──
    (sharedData.feed_views || []).forEach((v) => {
      sections["/feed"].push({
        id: `shared-feed-${v.document_id}`,
        label: v.name || "Untitled View",
        keywords: ["shared", v.owner_name || ""],
        shared: true,
        sharedBy: v.owner_name || "Unknown",
        icon: Rss,
        event: {
          navigateTo: "/feed",
          name: "sidebar:feed-switch-view",
          detail: { kind: "custom", view: v },
        },
      });
    });

    // ── Graph views ──
    (sharedData.graph_views || []).forEach((v) => {
      sections["/graph"].push({
        id: `shared-graph-${v.document_id}`,
        label: v.name || "Untitled View",
        keywords: ["shared", v.owner_name || ""],
        shared: true,
        sharedBy: v.owner_name || "Unknown",
        icon: Waypoints,
        event: {
          navigateTo: "/graph",
          name: "sidebar:graph-switch-source",
          detail: { kind: "custom_view", view: v },
        },
      });
    });

    // ── Wiki cases: green dot, no sub-sections (we don't have available data) ──
    (sharedData.wiki_cases || []).forEach((c) => {
      const companyName = c.target || "Unknown Company";

      sections["/wiki"].push({
        id: `shared-wiki-${companyName}`,
        label: companyName,
        keywords: ["shared", "company", c.owner_name || ""],
        shared: true,
        sharedBy: c.owner_name || "Unknown",
        statusDot: "#4ade80",
        event: {
          navigateTo: "/wiki",
          name: "sidebar:wiki-company-selected",
          detail: { company: companyName },
        },
      });
    });

    // Register under per-section keys so MainSearch can merge them
    for (const [section, items] of Object.entries(sections)) {
      if (items.length > 0) {
        register(`__shared:${section}`, items);
      } else {
        unregister(`__shared:${section}`);
      }
    }

    return () => {
      for (const section of Object.keys(sections)) {
        unregister(`__shared:${section}`);
      }
    };
  }, [sharedData, register, unregister]);
}
